import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import {
  sendTestMessage,
  uploadMedia,
  sendTestSurvey,
  sendTestVoiceRecording,
} from 'src/api/twilio';
import { sendDirectSndTestMessage } from 'src/api/direct_snd';
import { VALID_AUDIO_FILE_TYPES, ORG_SMS_PROVIDERS } from 'src/constants/misc';

export const useTestMessage = ({ provider } = {}) => {
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async ({
    from,
    selectedFile,
    message,
    successMsg,
    errorMsg,
    provider: providerOverride,
  }) => {
    try {
      formatPhoneNumberForTwilio(to);
    } catch {
      enqueueSnackbar('Invalid phone number', { variant: 'error' });
      return;
    }
    if (!from) {
      enqueueSnackbar('Sender phone number is required', { variant: 'error' });
      return;
    }
    if (!message) {
      enqueueSnackbar('Message is required', { variant: 'error' });
      return;
    }
    let mediaUrl = null;
    let res = null;
    try {
      setLoading(true);
      if (selectedFile) {
        const mediaData = await uploadMedia({ media_file: selectedFile });
        mediaUrl = mediaData.url;
      }
      const effectiveProvider = providerOverride ?? provider;
      if (effectiveProvider === ORG_SMS_PROVIDERS.TWILIO) {
        res = await sendTestMessage({ message, to, from, mediaUrl });
      } else {
        res = await sendDirectSndTestMessage({ message, to, from, mediaUrl });
      }
      enqueueSnackbar(successMsg ?? 'Test message sent', { variant: 'success' });
      setTo('');
    } catch {
      enqueueSnackbar(errorMsg ?? 'Failed to send test message', { variant: 'error' });
      return;
    } finally {
      setLoading(false);
    }
    return { mediaUrl, message, twilioSid: res?.sid };
  };

  return {
    to,
    setTo,
    loading,
    sendTestMessage: onSubmit,
  };
};

export const useTestSurvey = () => {
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async ({ twilioFlowSid, from, fromPhoneTwilioSid }) => {
    try {
      formatPhoneNumberForTwilio(to);
    } catch {
      enqueueSnackbar('Invalid phone number', { variant: 'error' });
      return;
    }
    if (!from || !fromPhoneTwilioSid) {
      enqueueSnackbar('Sender phone number is required', { variant: 'error' });
      return;
    }
    if (!twilioFlowSid) {
      enqueueSnackbar('Select a Survey to test', { variant: 'error' });
      return;
    }
    try {
      setLoading(true);
      await sendTestSurvey({ twilioFlowSid, from, fromPhoneTwilioSid, to });
      enqueueSnackbar('Test survey sent', { variant: 'success' });
      setTo('');
    } catch {
      enqueueSnackbar('Failed to send test message', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return {
    to,
    setTo,
    loading,
    sendTestSurvey: onSubmit,
  };
};

export const useTestVoiceRecording = () => {
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async ({ from, selectedFile }) => {
    try {
      formatPhoneNumberForTwilio(to);
    } catch {
      enqueueSnackbar('Invalid phone number', { variant: 'error' });
      return;
    }
    if (!from) {
      enqueueSnackbar('Sender phone number is required', { variant: 'error' });
      return;
    }
    if (!selectedFile || !VALID_AUDIO_FILE_TYPES.includes(selectedFile.type.split(';')[0])) {
      enqueueSnackbar('Audio recording/file is required', { variant: 'error' });
      return;
    }
    try {
      setLoading(true);
      const mediaData = await uploadMedia({ media_file: selectedFile });
      const mediaUrl = mediaData.url;
      await sendTestVoiceRecording({ to, from, mediaUrl });
      enqueueSnackbar('Test voice recording sent', { variant: 'success' });
      setTo('');
    } catch {
      enqueueSnackbar('Failed to send test voice recording', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return {
    to,
    setTo,
    loading,
    sendTestVoiceRecording: onSubmit,
  };
};

export function formatPhoneNumberForTwilio(phoneNumber, countryCode = '+1') {
  const cleanNumber = phoneNumber.replace(/[\s()-]/g, '');
  const match = cleanNumber.match(/^(?:\+?)([0-9]{3})([0-9]{3})([0-9]{4})$/);
  if (!match) {
    throw new Error('Invalid mobile number number format');
  }
  const [, areaCode, exchange, subscriber] = match;
  return `${countryCode}${areaCode}${exchange}${subscriber}`;
}

export const bulkSanitizeNumbers = (inputString) => {
  const numbers = inputString.split(',').map((s) => s.replace(/\+1|\s/g, '').replace(/\D/gm, ''));
  const valid = [];
  const invalid = [];

  numbers.forEach((num) => {
    try {
      valid.push(formatPhoneNumberForTwilio(num));
    } catch {
      invalid.push(num);
    }
  });
  return { valid: Array.from(new Set(valid)), invalid: Array.from(new Set(invalid)) };
};
